import type { Override } from "../../../../types/internal/utility";
import type { SaleProduct, SaleProductType } from "../../../Reducers/SellScreen/SellScreenSaleReducerTypes";
import ObjectPool from "../../Generic/Pools/ObjectPool";
import type { ComparisonProduct } from "./PromotionManager";

export interface SalePromotionProductPrice {
    price: number;
    quantity: number;
}

export type SalePromotionProduct = Override<SaleProduct, {
    case_quantity: number;
    is_case: boolean;
    caseQuantity?: number;
    isCase?: boolean;
    sortKey?: number;
    base?: number;
    everyday?: number;
    price?: number;
    prices: SaleProduct["prices"] & {
        compare?: Array<{
            quantity: number;
            price: number;
            source: string;
            compare?: number;
            everyday?: number;
        }>;
        used?: Array<string>;
        everyday: number;
    };
    price_points?: Array<SalePromotionProductPrice>;
}>;

export interface GroupedProduct {
    case_quantity: number;
    special: boolean;
    total_base_price: number;
    total_quantity: number;
    total_everyday_price: number;
    total_price: number;
    rate: number;
    everyday_rate: number;
}

export type GroupedProducts = Record<string, GroupedProduct>;

export const isSpecialProduct = (
    product: { uuid: string; special?: boolean; type?: SaleProductType; },
    onlySpecialIfGiftCard = false
): boolean => {
    if(onlySpecialIfGiftCard && product.type !== "GiftCard") {
        return false;
    }

    return typeof product.special !== "undefined" && product.special || product.type === "GiftCard";
};

export const groupProductsByUUID = (
    products: Array<SalePromotionProduct | SaleProduct | ComparisonProduct>,
    ignoreEdited = true,
    ignoreReturned = true,
    ignoreSpecial = false
): GroupedProducts => {
    const productsById = ObjectPool.Get() as GroupedProducts;

    for(let i = 0, l = products.length; i < l; i++) {
        const product = products[i];

        if(
            isSpecialProduct(product, ignoreSpecial) ||
            (
                product.edited &&
                ignoreEdited
            ) ||
            (
                ignoreReturned &&
                product.quantity <= 0
            )
        ) continue;

        const caseQuantity = "case_quantity" in product ? product.case_quantity : product.caseQuantity;

        if(typeof productsById[product.uuid] === "undefined") {
            productsById[product.uuid] = {
                case_quantity       : caseQuantity,
                special             : isSpecialProduct(products[i]),
                total_base_price    : 0,
                total_quantity      : 0,
                total_everyday_price: 0,
                total_price         : 0,
                rate                : 0,
                everyday_rate       : 0,
            };
        }

        let base: undefined | number = undefined;
        let everyday: undefined | number = undefined;
        let price: undefined | number = undefined;

        if("base" in product) {
            base = product.base;
            everyday = product.everyday;
            price = product.price;
        }

        if(typeof base === "undefined" || isNaN(base)) {
            base = product.prices.base;
            everyday = product.prices.normal;
            price = product.prices.price;
        }

        productsById[products[i].uuid].total_base_price += base;
        productsById[products[i].uuid].total_everyday_price += everyday as number;
        productsById[products[i].uuid].total_price += price as number;
        productsById[products[i].uuid].total_quantity += products[i].quantity;
        productsById[products[i].uuid].rate =
            productsById[products[i].uuid].total_base_price / productsById[products[i].uuid].total_quantity;
        productsById[products[i].uuid].everyday_rate =
            productsById[products[i].uuid].total_everyday_price / productsById[products[i].uuid].total_quantity;
    }

    return productsById;
};
